import {IconButton} from '@dropbox/dig-components/buttons';
import {Menu} from '@dropbox/dig-components/menu';
import {atoms} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {MoreVerticalLine} from '@dropbox/dig-icons/assets';
import {loggedInEmployeeAtom} from 'atoms/employee';
import {snackbarAtom} from 'atoms/snackbar';
import {ROUTE_PATHS} from 'constant';
import {t} from 'i18next';
import {useAtomValue, useSetAtom} from 'jotai';
import {useNavigate} from 'react-router';
import {usePinnedApps} from 'views/home/dashboard/pinnedApps';

import {AppDeletionAction} from './AppDeletionAction';
import {
  getAppDisplayName,
  getSafeAppLaunchUrl,
  getSafeReplitEditUrl,
  isPinnableApp,
} from './appUtils';
import {CatalogApp} from './catalogApp';
import {useAppVisibility} from './useAppVisibility';

export const AppActionsMenu = ({
  app,
  withManageActions = true,
}: {
  app: CatalogApp;
  /** Owner actions (edit, visibility, delete); off leaves only copy link and add to home. */
  withManageActions?: boolean;
}) => {
  const navigate = useNavigate();
  const setSnackbar = useSetAtom(snackbarAtom);
  const {employee} = useAtomValue(loggedInEmployeeAtom);
  const {slugs: pinnedSlugs, pinApp, unpinApp} = usePinnedApps(employee?.user_id);
  const launchUrl = getSafeAppLaunchUrl(app);
  const editUrl = withManageActions ? getSafeReplitEditUrl(app) : null;
  const canDelete = withManageActions && app.platform?.['.tag'] === 'company_os' && Boolean(app.id);
  const visibility = useAppVisibility(app);
  const canChangeVisibility = withManageActions && visibility.canChangeVisibility;
  const visibilityActionLabel = withManageActions ? visibility.visibilityActionLabel : null;
  const pinSlug = employee?.user_id && isPinnableApp(app) ? app.slug : null;
  const isPinned = pinSlug !== null && pinnedSlugs.includes(pinSlug);

  if (!launchUrl && !editUrl && !canDelete && !canChangeVisibility && !pinSlug) {
    return null;
  }

  const copyLink = async () => {
    if (!launchUrl) {
      return;
    }

    try {
      await navigator.clipboard.writeText(launchUrl);
      setSnackbar({text: t('copied')});
    } catch {
      setSnackbar({text: t('error_copying_link')});
    }
  };

  const togglePinned = () => {
    if (!pinSlug) {
      return;
    }
    const name = getAppDisplayName(app);
    if (isPinned) {
      unpinApp(pinSlug);
      setSnackbar({text: t('dashboard_apps_tile_removed', {name})});
    } else {
      pinApp(pinSlug);
      setSnackbar({
        text: t('dashboard_apps_tile_added', {name}),
        action: {text: t('apps_view_home'), to: ROUTE_PATHS.HOME},
      });
    }
  };

  const hasPrimaryActions =
    canDelete ||
    Boolean(launchUrl) ||
    Boolean(editUrl) ||
    Boolean(visibilityActionLabel) ||
    Boolean(pinSlug);

  return (
    <AppDeletionAction app={app}>
      {(requestDelete) => (
        <Menu.Wrapper
          closeOnSelection
          onSelection={(value) => {
            if (value === 'update' && app.id) {
              navigate(ROUTE_PATHS.APPS_UPDATE.replace(':siteId', app.id), {state: {app}});
            } else if (value === 'edit_replit' && editUrl) {
              window.open(editUrl, '_blank', 'noopener,noreferrer');
            } else if (value === 'copy_link' && launchUrl) {
              void copyLink();
            } else if (value === 'toggle_home' && pinSlug) {
              togglePinned();
            } else if (value === 'toggle_visibility' && canChangeVisibility) {
              visibility.toggleVisibility();
            } else if (value === 'delete') {
              requestDelete();
            }
          }}
        >
          {({getContentProps, getTriggerProps}) => (
            <>
              <IconButton
                {...getTriggerProps()}
                aria-label={t('more_options')}
                className={atoms({display: 'block'})}
                size="small"
                variant="borderless"
              >
                <UIIcon src={MoreVerticalLine} className={atoms({color: 'Text Subtle'})} />
              </IconButton>
              <Menu.Content {...getContentProps()} placement="bottom-end">
                {hasPrimaryActions ? (
                  <Menu.Segment>
                    {canDelete ? <Menu.ActionItem value="update" withTitle={t('edit')} /> : null}
                    {editUrl ? (
                      <Menu.ActionItem value="edit_replit" withTitle={t('apps_edit_in_replit')} />
                    ) : null}
                    {pinSlug ? (
                      <Menu.ActionItem
                        value="toggle_home"
                        withTitle={isPinned ? t('apps_remove_from_home') : t('apps_add_to_home')}
                      />
                    ) : null}
                    {launchUrl ? (
                      <Menu.ActionItem value="copy_link" withTitle={t('copy_link')} />
                    ) : null}
                    {visibilityActionLabel ? (
                      <Menu.ActionItem
                        value="toggle_visibility"
                        withTitle={visibilityActionLabel}
                      />
                    ) : null}
                  </Menu.Segment>
                ) : null}
                {canDelete ? (
                  <Menu.Segment>
                    <Menu.ActionItem value="delete" withTitle={t('delete')} />
                  </Menu.Segment>
                ) : null}
              </Menu.Content>
            </>
          )}
        </Menu.Wrapper>
      )}
    </AppDeletionAction>
  );
};
